import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { notificationsAPI } from '../utils/api';
import { formatDistanceToNow } from 'date-fns';

const INITIAL_LIMIT = 3;
const MORE_LIMIT = 10;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all');
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Cursor = created_at of the oldest loaded notification
  const cursorRef = useRef(null);
  const ref = useRef(null);

  // ── load helpers ──────────────────────────────────────────────────────────

  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      cursorRef.current = null;
      const [notifRes, countRes] = await Promise.all([
        notificationsAPI.getAll(null, INITIAL_LIMIT),
        notificationsAPI.getUnreadCount(),
      ]);
      const { items, has_more } = notifRes.data;
      setNotifications(items);
      setHasMore(has_more);
      setUnreadCount(countRes.data.count);
      cursorRef.current = items.length ? items[items.length - 1].created_at : null;
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await notificationsAPI.getAll(cursorRef.current, MORE_LIMIT);
      const { items, has_more } = res.data;
      setNotifications(prev => [...prev, ...items]);
      setHasMore(has_more);
      if (items.length) cursorRef.current = items[items.length - 1].created_at;
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  }, [loadingMore, hasMore]);

  // ── polling for badge count (no full reload) ──────────────────────────────
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const res = await notificationsAPI.getUnreadCount();
        setUnreadCount(res.data.count);
      } catch { /* ignore */ }
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load when panel first opens
  useEffect(() => {
    if (open) loadInitial();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── mark-read actions ─────────────────────────────────────────────────────

  const handleMarkRead = async (notif) => {
    if (!notif.read) {
      try {
        await notificationsAPI.markRead(notif.notification_id);
        setNotifications(prev => prev.map(n =>
          n.notification_id === notif.notification_id ? { ...n, read: true } : n
        ));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch { /* ignore */ }
    }
    if (notif.url) {
      setOpen(false);
      navigate(notif.url);
    }
  };

  const handleMarkReadOnly = async (e, notif) => {
    e.stopPropagation();
    if (notif.read) return;
    try {
      await notificationsAPI.markRead(notif.notification_id);
      const newCount = Math.max(0, unreadCount - 1);
      setNotifications(prev => prev.map(n =>
        n.notification_id === notif.notification_id ? { ...n, read: true } : n
      ));
      setUnreadCount(newCount);
      if (newCount === 0) setFilter('all');
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      setFilter('all');
    } catch { /* ignore */ }
  };

  const handleClearAll = async () => {
    try {
      await notificationsAPI.clearAll();
      setNotifications([]);
      setUnreadCount(0);
      setHasMore(false);
      cursorRef.current = null;
    } catch { /* ignore */ }
  };

  // ── derived list ─────────────────────────────────────────────────────────

  const filtered = filter === 'unread'
    ? notifications.filter(n => !n.read)
    : notifications;

  const formatTime = (ts) => {
    try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); }
    catch { return ''; }
  };

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell-btn"
        className="relative p-1.5 rounded-full hover:bg-slate-50 transition-colors"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-rose-500 text-white text-[10px] font-bold px-1" data-testid="unread-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="
            fixed left-3 right-3 top-14 z-50
            sm:absolute sm:fixed-none sm:left-auto sm:right-0 sm:top-full sm:mt-1 sm:w-80
            bg-white dark:bg-slate-800 border border-slate-200 dark:border-white/10
            rounded-xl shadow-2xl overflow-hidden
          "
          data-testid="notification-panel"
        >
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button onClick={handleMarkAllRead} data-testid="mark-all-read-btn"
                  className="text-xs text-green-600 hover:text-green-700 font-medium">
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button onClick={handleClearAll} data-testid="clear-all-btn"
                  className="text-xs text-slate-400 hover:text-rose-500 font-medium transition-colors">
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-slate-100">
            <button onClick={() => setFilter('all')} data-testid="filter-all-btn"
              className={`flex-1 py-2 text-xs font-medium transition-colors ${
                filter === 'all' ? 'text-green-700 border-b-2 border-green-600' : 'text-slate-500 hover:text-slate-700'
              }`}>All</button>
            <button onClick={() => setFilter('unread')} data-testid="filter-unread-btn"
              className={`flex-1 py-2 text-xs font-medium transition-colors ${
                filter === 'unread' ? 'text-green-700 border-b-2 border-green-600' : 'text-slate-500 hover:text-slate-700'
              }`}>Unread{unreadCount > 0 ? ` (${unreadCount})` : ''}</button>
          </div>

          {/* List */}
          <div className="max-h-[60vh] sm:max-h-80 overflow-y-auto" data-testid="notification-list">
            {loading ? (
              <div className="py-8 flex justify-center">
                <span className="animate-spin w-5 h-5 border-2 border-slate-200 border-t-green-600 rounded-full" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm text-slate-400">
                  {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
                </p>
              </div>
            ) : (
              <>
                {filtered.map(notif => (
                  <div
                    key={notif.notification_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleMarkRead(notif)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleMarkRead(notif); }}
                    data-testid={`notification-item-${notif.notification_id}`}
                    className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 cursor-pointer ${
                      !notif.read ? 'bg-green-50' : ''
                    }`}
                  >
                    <div className="flex-shrink-0 mt-1">
                      {!notif.read ? (
                        <button
                          onClick={(e) => handleMarkReadOnly(e, notif)}
                          data-testid={`mark-read-dot-${notif.notification_id}`}
                          title="Mark as read"
                          className="w-2 h-2 rounded-full bg-green-500 hover:bg-green-700 transition-colors focus:outline-none"
                        />
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-transparent" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notif.read ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
                        {notif.title}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{notif.body}</p>
                      <p className="text-[10px] text-slate-400 mt-1">{formatTime(notif.created_at)}</p>
                    </div>
                  </div>
                ))}

                {/* Previous notifications button */}
                {hasMore && filter === 'all' && (
                  <div className="px-4 py-3 border-t border-slate-100">
                    <button
                      onClick={loadMore}
                      disabled={loadingMore}
                      data-testid="load-more-notifications-btn"
                      className="w-full text-xs text-slate-500 hover:text-green-700 font-medium py-1 flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors"
                    >
                      {loadingMore
                        ? <><span className="animate-spin inline-block w-3 h-3 border border-current border-t-transparent rounded-full" /> Loading...</>
                        : 'Previous notifications'
                      }
                    </button>
                  </div>
                )}

                {!hasMore && filtered.length > 0 && filter === 'all' && (
                  <p className="py-3 text-center text-[10px] text-slate-400">All notifications loaded</p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
